import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { cartAPI, contactAPI, wishlistAPI } from '../config/api';
import resolveImageUrl from '../utils/resolveImageUrl';
import {
  addDays,
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDefaultDropoffDate,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
  getTomorrowDate,
  toISODate,
} from '../utils/rentalFields';
import {
  buildQuoteMessage,
  isDuplicateQuoteRequest,
  rememberQuoteRequest,
} from '../utils/quoteRequests';
import { showAppToast } from '../utils/notifications';

const dashboardViews = new Set(['overview', 'bookings', 'wishlist', 'checkout', 'fleet', 'support', 'vehicle']);

const serviceCards = [
  {
    title: 'Premium fleet',
    text: 'Browse executive, SUV, hybrid, and luxury vehicles prepared for business and leisure travel.',
    icon: 'bi-car-front-fill',
    to: '/dashboard/fleet',
  },
  {
    title: 'Booking support',
    text: 'Keep pickup dates, drop-off plans, and account details clear before handover.',
    icon: 'bi-calendar-check-fill',
    to: '/dashboard/bookings',
  },
  {
    title: 'Royal assistance',
    text: 'Reach the DeRoyalty team for route questions, service requests, and rental guidance.',
    icon: 'bi-headset',
    to: '/dashboard/support',
  },
];

const bookingRequirements = [
  { title: "Driver's licence", text: 'Valid and held for at least two years.', icon: 'bi-person-vcard' },
  { title: 'Minimum age', text: 'Primary drivers must be 25 or older.', icon: 'bi-calendar-check' },
  { title: 'Photo ID', text: 'National ID or passport ready for pickup verification.', icon: 'bi-passport' },
  { title: 'Proof of residence', text: 'Recent utility bill or official letter with your current address.', icon: 'bi-house-check' },
];

function getBookingStatus(item) {
  return item?.bookingStatus || item?.booking_status || 'Pending Review';
}

function vehiclePath(product) {
  return `/dashboard/fleet/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`;
}

function getUserName(user) {
  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  return fullName || user?.name || user?.email || '';
}

function getProductStatus(product) {
  return product?.bookingStatus || product?.booking_status || 'Available';
}

function DashboardEmptyState({ icon, title, text, actionLabel, actionTo }) {
  return (
    <div className="dashboard-empty-state">
      <i className={`bi ${icon}`} />
      <strong>{title}</strong>
      <span>{text}</span>
      {actionLabel && actionTo && (
        <Link to={actionTo} className="btn-luxury">{actionLabel}</Link>
      )}
    </div>
  );
}

function UserDashboard({
  cartItems = [],
  totalCost = '0.00',
  products = [],
  activeView = 'overview',
  vehicleId = null,
  currentUser = null,
  onCartUpdate,
}) {
  const params = useParams();
  const [wishlist, setWishlist] = useState([]);
  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('10:00');
  const [requirementsAccepted, setRequirementsAccepted] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: getUserName(currentUser),
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    city: '',
    country: '',
    message: '',
  });
  const currentView = dashboardViews.has(activeView) ? activeView : 'overview';
  const activeVehicleId = vehicleId || params.id;
  const minPickupDate = getTomorrowDate();
  const dropoffMinDate = pickupDate ? getDefaultDropoffDate(pickupDate) : minPickupDate;

  useEffect(() => {
    let active = true;
    Promise.resolve(wishlistAPI.list())
      .then((response) => {
        if (active) setWishlist(response?.data || []);
      })
      .catch(() => {
        if (active) setWishlist([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    setSupportForm((previous) => ({
      ...previous,
      name: previous.name || getUserName(currentUser),
      email: previous.email || currentUser.email || '',
      phone: previous.phone || currentUser.phone || '',
    }));
  }, [currentUser]);

  const bookings = useMemo(() => cartItems || [], [cartItems]);
  const availableFleet = useMemo(() => (
    (products || []).filter((product) => getProductStatus(product).toLowerCase() === 'available')
  ), [products]);
  const selectedVehicle = useMemo(() => (
    (products || []).find((product) => String(product.id) === String(activeVehicleId))
  ), [activeVehicleId, products]);
  const bookedDays = bookings.reduce((sum, item) => sum + getRentalDays(item), 0);
  const computedTotal = bookings.reduce((sum, item) => sum + getRentalLineTotal(item), 0);
  const displayTotal = Number(totalCost || 0) > 0 ? Number(totalCost) : computedTotal;
  const detailRentalDays = useMemo(() => {
    if (!pickupDate || !dropoffDate) return 1;
    const diff = new Date(dropoffDate) - new Date(pickupDate);
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(1, days || 1);
  }, [pickupDate, dropoffDate]);

  const progressItems = [
    { label: 'Booking requests', value: bookings.length, percent: Math.min(100, bookings.length * 34), icon: 'bi-calendar2-check' },
    { label: 'Rental days planned', value: bookedDays, percent: Math.min(100, bookedDays * 8), icon: 'bi-clock-history' },
    { label: 'Saved vehicles', value: wishlist.length, percent: Math.min(100, wishlist.length * 20), icon: 'bi-heart' },
    { label: 'Fleet options', value: availableFleet.length, percent: Math.min(100, availableFleet.length * 12), icon: 'bi-car-front' },
  ];

  const goalSteps = [
    { label: 'Book a Vehicle', to: '/dashboard/fleet', active: currentView === 'overview' || currentView === 'fleet' },
    { label: 'Rental Desk', to: '/dashboard/bookings', active: currentView === 'bookings' },
    { label: 'Saved Fleet', to: '/dashboard/wishlist', active: currentView === 'wishlist' },
    { label: 'Support', to: '/dashboard/support', active: currentView === 'support' },
  ];

  const handlePickupChange = (value) => {
    setPickupDate(value);
    if (dropoffDate && new Date(dropoffDate) <= new Date(value)) {
      setDropoffDate(toISODate(addDays(value, 1)));
    }
  };

  const handleDashboardBooking = async (event) => {
    event.preventDefault();
    if (!selectedVehicle) return;

    if (!pickupDate || !dropoffDate || !dropoffTime) {
      showAppToast({ message: 'Please select pickup date, dropoff date, and dropoff time.', type: 'warning', duration: 3000 });
      return;
    }

    if (new Date(dropoffDate) <= new Date(pickupDate)) {
      showAppToast({ message: 'Dropoff date must be after pickup date.', type: 'warning', duration: 3000 });
      return;
    }

    if (!requirementsAccepted) {
      showAppToast({ message: 'Please confirm you have the required documents before booking.', type: 'warning', duration: 3000 });
      return;
    }

    setBookingLoading(true);
    try {
      await cartAPI.add({
        productId: selectedVehicle.id,
        quantity: detailRentalDays,
        bookedFrom: pickupDate,
        bookedFor: dropoffDate,
        dropoffTime,
      });
      await onCartUpdate?.();
      showAppToast({ message: 'Booking request added to your dashboard.', type: 'success', duration: 2500 });
      setPickupDate('');
      setDropoffDate('');
      setDropoffTime('10:00');
      setRequirementsAccepted(false);
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to add booking.', type: 'error', duration: 3400 });
    } finally {
      setBookingLoading(false);
    }
  };

  const handleDashboardWishlist = async () => {
    if (!selectedVehicle) return;

    setWishlistLoading(true);
    try {
      await wishlistAPI.add({ productId: selectedVehicle.id });
      const response = await wishlistAPI.list();
      setWishlist(response?.data || []);
      showAppToast({ message: 'Vehicle saved to your wishlist.', type: 'success', duration: 2200 });
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to save vehicle.', type: 'error', duration: 3400 });
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleSupportChange = (event) => {
    const { name, value } = event.target;
    setSupportForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSupportSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...supportForm,
      message: buildQuoteMessage({
        message: supportForm.message,
        requestSource: 'Customer dashboard support request',
      }),
    };

    if (isDuplicateQuoteRequest(payload)) {
      showAppToast({ message: 'A matching support or quote request was already sent from this device.', type: 'info', duration: 3200 });
      return;
    }

    setSupportLoading(true);
    try {
      await contactAPI.submit(payload);
      rememberQuoteRequest(payload);
      showAppToast({ message: 'Support request sent. Our team will respond shortly.', type: 'success', duration: 2500 });
      setSupportForm((previous) => ({ ...previous, message: '' }));
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to send support request.', type: 'error', duration: 3400 });
    } finally {
      setSupportLoading(false);
    }
  };

  const renderBookingRows = (limit) => {
    const bookingRows = limit ? bookings.slice(0, limit) : bookings;

    if (!bookingRows.length) {
      return (
        <DashboardEmptyState
          icon="bi-calendar-plus"
          title="No active bookings yet"
          text="Start with the fleet tab and select the vehicle that fits your journey."
          actionLabel="Book Now"
          actionTo="/dashboard/fleet"
        />
      );
    }

    return bookingRows.map((item) => {
      const product = item.product || {};
      const detailTo = product.id ? vehiclePath(product) : '/dashboard/fleet';

      return (
        <article key={item.id} className="user-dashboard-booking-card user-dashboard-record-card">
          <div>
            <strong>{product.name || 'Vehicle booking'}</strong>
            <span>
              {formatRentalDate(getBookedFrom(item))} to {formatRentalDate(getBookedFor(item))}
              {getDropoffTime(item) ? ` at ${getDropoffTime(item)}` : ''}
            </span>
          </div>
          <div>
            <span>{getBookingStatus(item)}</span>
            <strong>{formatMoney(getRentalLineTotal(item))}</strong>
            <Link to={detailTo} className="admin-action-link">
              View vehicle
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </Link>
          </div>
        </article>
      );
    });
  };

  const renderOverview = () => (
    <>
      <section className="dashboard-goal-hero user-goal-hero">
        <h1 className="section-title text-primary-theme">Hi, welcome back!</h1>
        <p className="text-secondary-theme">What&apos;s your rental goal today?</p>
        <nav className="dashboard-goal-steps" aria-label="Customer rental workflow">
          {goalSteps.map((step, index) => (
            <React.Fragment key={step.label}>
              <Link to={step.to} className={`dashboard-goal-step ${step.active ? 'active' : ''}`}>
                {step.label}
              </Link>
              {index < goalSteps.length - 1 && <span className="dashboard-goal-connector" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </nav>
      </section>

      <section className="dashboard-focus-grid user-dashboard-main-grid">
        <div className="dashboard-progress-card user-dashboard-progress-card">
          <div className="dashboard-progress-heading">Rental progress</div>
          <div className="user-dashboard-progress-list">
            {progressItems.map((item) => (
              <div key={item.label} className="user-dashboard-progress-row">
                <i className={`bi ${item.icon}`} />
                <div>
                  <span>{item.label}</span>
                  <div><span style={{ width: `${item.percent}%` }} /></div>
                </div>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-focus-panel user-dashboard-action-panel">
          <div className="dashboard-focus-copy">
            <span className="dashboard-mini-label">Recommended next step</span>
            <h2>Confirm dates before vehicle handover</h2>
            <p>
              Keep pickup and drop-off details accurate so the team can prepare the exact vehicle and service flow.
            </p>
            <div className="user-dashboard-hero-actions">
              <Link to="/dashboard/fleet" className="btn-luxury">View Our Fleet</Link>
              <Link to="/dashboard/bookings" className="btn-luxury-outline">Manage Bookings</Link>
            </div>
          </div>
          <div className="dashboard-feature-visual" aria-hidden="true">
            <div className="dashboard-feature-score">{bookings.length}</div>
            <span>Active bookings</span>
            <i className="bi bi-calendar2-check-fill" />
          </div>
        </div>
      </section>

      <section className="user-dashboard-stat-grid" aria-label="Rental dashboard summary">
        <article>
          <span>Active bookings</span>
          <strong>{bookings.length}</strong>
        </article>
        <article>
          <span>Planned value</span>
          <strong>{formatMoney(displayTotal)}</strong>
        </article>
        <article>
          <span>Available fleet</span>
          <strong>{availableFleet.length}</strong>
        </article>
        <article>
          <span>Saved vehicles</span>
          <strong>{wishlist.length}</strong>
        </article>
      </section>

      <section className="user-dashboard-section-grid">
        <div className="dashboard-panel">
          <div className="admin-card-header">
            <h2 className="section-title text-primary-theme">Current bookings</h2>
            <Link to="/dashboard/bookings" className="admin-action-link">View all</Link>
          </div>
          <div className="user-dashboard-booking-list">{renderBookingRows(4)}</div>
        </div>

        <div className="dashboard-panel">
          <div className="admin-card-header">
            <h2 className="section-title text-primary-theme">Services</h2>
            <Link to="/dashboard/support" className="admin-action-link">Get a Quote</Link>
          </div>
          <div className="user-dashboard-service-list">
            {serviceCards.map((card) => (
              <Link to={card.to} key={card.title} className="user-dashboard-service-card">
                <i className={`bi ${card.icon}`} />
                <div>
                  <strong>{card.title}</strong>
                  <span>{card.text}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );

  const renderBookings = () => (
    <section className="dashboard-panel user-dashboard-tab-panel">
      <div className="admin-card-header">
        <div>
          <span className="dashboard-mini-label">Rental desk</span>
          <h1 className="section-title text-primary-theme">My Booking Desk</h1>
          <p className="text-secondary-theme">Review vehicle requests, pickup dates, drop-off plans, and rental value from your account workspace.</p>
        </div>
        <Link to="/dashboard/fleet" className="btn-luxury">Book Another Vehicle</Link>
      </div>
      <div className="user-dashboard-record-list">{renderBookingRows()}</div>
    </section>
  );

  const renderWishlist = () => (
    <section className="dashboard-panel user-dashboard-tab-panel">
      <div className="admin-card-header">
        <div>
          <span className="dashboard-mini-label">Saved fleet</span>
          <h1 className="section-title text-primary-theme">Wishlist</h1>
          <p className="text-secondary-theme">Keep preferred vehicles ready for your next booking request.</p>
        </div>
        <Link to="/dashboard/fleet" className="btn-luxury-outline">Browse Fleet</Link>
      </div>
      {wishlist.length ? (
        <div className="user-dashboard-fleet-grid">
          {wishlist.map((item) => {
            const product = item.product || {};
            const detailTo = product.id ? vehiclePath(product) : '/dashboard/fleet';

            return (
              <article key={item.id} className="user-dashboard-fleet-card">
                <img src={resolveImageUrl(product.imageURL)} alt={product.name || 'Saved vehicle'} />
                <div>
                  <strong>{product.name || 'Saved vehicle'}</strong>
                  <span>{product.description || 'Premium vehicle saved for quick access.'}</span>
                  <Link to={detailTo} className="admin-action-link">
                    View details
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <DashboardEmptyState
          icon="bi-heart"
          title="No saved vehicles yet"
          text="Save vehicles from the fleet page and they will appear in this dashboard tab."
          actionLabel="Explore Fleet"
          actionTo="/dashboard/fleet"
        />
      )}
    </section>
  );

  const renderCheckout = () => (
    <section className="dashboard-panel user-dashboard-tab-panel">
      <div className="admin-card-header">
        <div>
          <span className="dashboard-mini-label">Checkout</span>
          <h1 className="section-title text-primary-theme">Confirm Rental Summary</h1>
          <p className="text-secondary-theme">Check the booking total before continuing to the secure confirmation step.</p>
        </div>
        <strong className="user-dashboard-total-pill">{formatMoney(displayTotal)}</strong>
      </div>
      <div className="user-dashboard-checkout-grid">
        <div className="user-dashboard-booking-list">{renderBookingRows()}</div>
        <aside className="user-dashboard-summary-card">
          <span>Vehicles</span>
          <strong>{bookings.length}</strong>
          <span>Rental days</span>
          <strong>{bookedDays}</strong>
          <span>Total value</span>
          <strong>{formatMoney(displayTotal)}</strong>
          <Link to={bookings.length ? '/dashboard/checkout' : '/dashboard/fleet'} className="btn-luxury">
            {bookings.length ? 'Ready for confirmation' : 'Choose a Vehicle'}
          </Link>
        </aside>
      </div>
    </section>
  );

  const renderFleet = () => (
    <section className="dashboard-panel user-dashboard-tab-panel">
      <div className="admin-card-header">
        <div>
          <span className="dashboard-mini-label">Fleet</span>
          <h1 className="section-title text-primary-theme">Available Fleet</h1>
          <p className="text-secondary-theme">Select a premium vehicle and continue into the booking flow.</p>
        </div>
        <Link to="/dashboard/fleet" className="btn-luxury-outline">Open Full Fleet</Link>
      </div>
      <div className="user-dashboard-fleet-grid">
        {availableFleet.slice(0, 12).map((product) => (
          <article key={product.id} className="user-dashboard-fleet-card">
            <img src={resolveImageUrl(product.imageURL)} alt={product.name || 'Vehicle'} />
            <div>
              <strong>{product.name}</strong>
              <span>{product.description || 'Premium vehicle available for rent.'}</span>
              <div className="user-dashboard-fleet-meta">
                <span>{product.year || 'Fleet'}</span>
                <span>{formatMoney(product.price)}/day</span>
              </div>
              <Link to={vehiclePath(product)} className="admin-action-link" aria-label={`View details for ${product.name}`}>
                View details
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );

  const renderVehicleDetail = () => {
    if (!selectedVehicle) {
      return (
        <section className="dashboard-panel user-dashboard-tab-panel">
          <DashboardEmptyState
            icon="bi-car-front"
            title="Vehicle not found"
            text="Return to the fleet tab and choose an available vehicle."
            actionLabel="Back to Fleet"
            actionTo="/dashboard/fleet"
          />
        </section>
      );
    }

    return (
      <section className="dashboard-panel user-dashboard-tab-panel user-dashboard-vehicle-detail">
        <div className="admin-card-header">
          <div>
            <Link to="/dashboard/fleet" className="admin-action-link">
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to fleet
            </Link>
            <h1 className="section-title text-primary-theme">{selectedVehicle.name}</h1>
            <p className="text-secondary-theme">{selectedVehicle.categoryName || 'Luxury rental vehicle'}</p>
          </div>
          <strong className="user-dashboard-total-pill">{formatMoney(selectedVehicle.price)}/day</strong>
        </div>

        <div className="user-dashboard-detail-layout">
          <div className="user-dashboard-detail-media">
            <img src={resolveImageUrl(selectedVehicle.imageURL)} alt={selectedVehicle.name} />
          </div>
          <div className="user-dashboard-detail-panel">
            <p>{selectedVehicle.description || 'Premium vehicle prepared for executive and leisure travel.'}</p>
            <div className="user-dashboard-fleet-meta">
              <span>{selectedVehicle.year || 'Fleet'}</span>
              <span>{getProductStatus(selectedVehicle)}</span>
              <span>{selectedVehicle.categoryName || 'Premium'}</span>
            </div>
            <div className="user-dashboard-requirements">
              <h2>Before you book</h2>
              <div className="user-dashboard-requirement-grid">
                {bookingRequirements.map((requirement) => (
                  <article key={requirement.title}>
                    <i className={`bi ${requirement.icon}`} />
                    <strong>{requirement.title}</strong>
                    <span>{requirement.text}</span>
                  </article>
                ))}
              </div>
              <label>
                <input
                  type="checkbox"
                  checked={requirementsAccepted}
                  onChange={(event) => setRequirementsAccepted(event.target.checked)}
                />
                <span>I confirm I have the required documents.</span>
              </label>
            </div>
            <form className="user-dashboard-booking-form" onSubmit={handleDashboardBooking}>
              <label>
                <span>Pickup date</span>
                <input type="date" min={minPickupDate} value={pickupDate} onChange={(event) => handlePickupChange(event.target.value)} required />
              </label>
              <label>
                <span>Drop-off date</span>
                <input type="date" min={dropoffMinDate} value={dropoffDate} onChange={(event) => setDropoffDate(event.target.value)} required />
              </label>
              <label>
                <span>Drop-off time</span>
                <input type="time" value={dropoffTime} onChange={(event) => setDropoffTime(event.target.value)} required />
              </label>
              <button type="submit" className="btn-luxury" disabled={bookingLoading}>
                {bookingLoading ? 'Adding...' : 'Book Now'}
              </button>
              <button type="button" className="btn-luxury-outline" onClick={handleDashboardWishlist} disabled={wishlistLoading}>
                {wishlistLoading ? 'Saving...' : 'Save Vehicle'}
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  };

  const renderSupport = () => (
    <section className="dashboard-panel user-dashboard-tab-panel">
      <div className="admin-card-header">
        <div>
          <span className="dashboard-mini-label">Support</span>
          <h1 className="section-title text-primary-theme">Rental Support</h1>
          <p className="text-secondary-theme">Get help with quotes, booking questions, pickup coordination, and account guidance.</p>
          <div className="user-dashboard-support-intro" aria-label="Support response coverage">
            <span><i className="bi bi-clock-history" aria-hidden="true" />24/7 rental desk</span>
            <span><i className="bi bi-receipt" aria-hidden="true" />Quote follow-up</span>
            <span><i className="bi bi-geo-alt" aria-hidden="true" />Pickup guidance</span>
          </div>
        </div>
        <Link to="/dashboard/fleet" className="btn-luxury">View Fleet</Link>
      </div>
      <div className="user-dashboard-support-grid user-dashboard-support-grid--polished">
        <div className="user-dashboard-service-list user-dashboard-service-list--support">
          {serviceCards.map((card) => (
            <Link to={card.to} key={card.title} className="user-dashboard-service-card">
              <i className={`bi ${card.icon}`} />
              <div>
                <strong>{card.title}</strong>
                <span>{card.text}</span>
              </div>
            </Link>
          ))}
        </div>
        <form className="user-dashboard-support-form" onSubmit={handleSupportSubmit}>
          <h2>Ask DeRoyalty support</h2>
          <label>
            <span>Name</span>
            <input name="name" value={supportForm.name} onChange={handleSupportChange} required />
          </label>
          <label>
            <span>Email</span>
            <input name="email" type="email" value={supportForm.email} onChange={handleSupportChange} required />
          </label>
          <div className="user-dashboard-support-form-row">
            <label>
              <span>City</span>
              <input name="city" value={supportForm.city} onChange={handleSupportChange} placeholder="Harare" required />
            </label>
            <label>
              <span>Phone</span>
              <input name="phone" value={supportForm.phone} onChange={handleSupportChange} placeholder="+263..." required />
            </label>
          </div>
          <label>
            <span>Country</span>
            <input name="country" value={supportForm.country} onChange={handleSupportChange} placeholder="Zimbabwe" required />
          </label>
          <label>
            <span>Request details</span>
            <textarea
              name="message"
              value={supportForm.message}
              onChange={handleSupportChange}
              rows={4}
              placeholder="Tell us whether this is about a booking, quote, pickup, return, or account support..."
              required
            />
          </label>
          <button type="submit" className="btn-luxury" disabled={supportLoading}>
            {supportLoading ? 'Sending...' : 'Send Support Request'}
          </button>
        </form>
      </div>
    </section>
  );

  const renderActiveView = () => {
    if (currentView === 'bookings') return renderBookings();
    if (currentView === 'wishlist') return renderWishlist();
    if (currentView === 'checkout') return renderCheckout();
    if (currentView === 'fleet') return renderFleet();
    if (currentView === 'vehicle') return renderVehicleDetail();
    if (currentView === 'support') return renderSupport();
    return renderOverview();
  };

  return (
    <div className="admin-layout user-dashboard-shell">
      <DashboardSidebar variant="user" cartCount={bookings.length} currentUser={currentUser} />
      <div className="admin-content user-dashboard-content">
        <div className="dashboard-page-inner">
          {renderActiveView()}
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
